import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Github, Linkedin, Mail, Download, MapPin } from "lucide-react";
import HeroAvatar from "../HeroAvatar";
import { absoluteUrl } from "../../lib/api";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

const Hero = ({ hero = {}, settings = {} }) => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const socials = hero.socials || {};
  const stats = hero.stats || [];
  const showAvatar = settings.show_avatar !== false;

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-32 pb-20 px-5 md:px-10"
      data-testid="hero-section"
    >
      <div className="max-w-6xl mx-auto w-full grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {showAvatar && (
          <motion.div
            {...fade(0.2)}
            className="lg:col-span-5 lg:order-2 flex justify-center"
            data-testid="hero-avatar-wrap"
          >
            <HeroAvatar src={absoluteUrl(hero.avatar_url || "/avatar.jpg")} />
          </motion.div>
        )}

        <div className={`${showAvatar ? "lg:col-span-7" : "lg:col-span-12"} lg:order-1`}>
          <motion.div
            {...fade(0)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-7"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-dot" />
            <span className="font-mono text-[11px] tracking-[0.25em] text-cyan-300 uppercase">
              {hero.badge_text || "Available"}
            </span>
          </motion.div>

          <motion.h1
            {...fade(0.08)}
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-7xl xl:text-8xl tracking-tighter font-light leading-[0.95] text-white"
            data-testid="hero-name"
          >
            <span className="block">{hero.first_name || "Hello"}</span>
            <span className="block text-gradient-accent font-medium">
              {((hero.name || "").replace(`${hero.first_name || ""} `, "") || ".")}
              <span className="text-cyan-400">.</span>
            </span>
          </motion.h1>

          <motion.p
            {...fade(0.18)}
            className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed"
            data-testid="hero-summary"
          >
            {hero.summary}
          </motion.p>

          <motion.div
            {...fade(0.28)}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              onClick={() => scrollTo("projects")}
              data-testid="hero-cta-projects"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-medium text-sm hover:shadow-[0_0_40px_rgba(0,240,255,0.4)] transition-all"
            >
              View Projects
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            {hero.resume_url && (
              <a
                href={hero.resume_url}
                download
                data-testid="hero-cta-resume"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full glass text-white text-sm font-medium hover:border-cyan-400/40 transition-all glass-hover"
              >
                <Download size={15} /> Download Resume
              </a>
            )}
            <button
              onClick={() => scrollTo("contact")}
              data-testid="hero-cta-contact"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm text-slate-300 hover:text-white transition-colors"
            >
              Get in touch <ArrowRight size={14} />
            </button>
          </motion.div>

          <motion.div
            {...fade(0.36)}
            className="mt-7 flex flex-wrap items-center gap-5 text-xs text-slate-500"
          >
            {hero.location && (
              <span className="inline-flex items-center gap-2">
                <MapPin size={13} /> {hero.location}
              </span>
            )}
            {socials.github && (
              <a href={socials.github} target="_blank" rel="noreferrer" data-testid="hero-github" className="inline-flex items-center gap-2 hover:text-white transition-colors">
                <Github size={14} /> GitHub
              </a>
            )}
            {socials.linkedin && (
              <a href={socials.linkedin} target="_blank" rel="noreferrer" data-testid="hero-linkedin" className="inline-flex items-center gap-2 hover:text-white transition-colors">
                <Linkedin size={14} /> LinkedIn
              </a>
            )}
            {hero.email && (
              <a href={`mailto:${hero.email}`} data-testid="hero-email" className="inline-flex items-center gap-2 hover:text-white transition-colors">
                <Mail size={14} /> Email
              </a>
            )}
          </motion.div>

          {stats.length > 0 && (
            <motion.div
              {...fade(0.45)}
              className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl"
            >
              {stats.map((s, i) => (
                <motion.div
                  key={s.label + i}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 + i * 0.06 }}
                  className="glass glass-hover rounded-xl p-3.5"
                  data-testid={`stat-${(s.label || "").toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="font-display text-xl md:text-2xl font-medium text-white tracking-tight">{s.value}</div>
                  <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-slate-500 mt-1">{s.label}</div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-slate-500">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-cyan-400/60 to-transparent"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
